import { motion } from "motion/react";

const aboutParagraphs = [
  "KIVAS brings creativity and intelligence together to transform brands through immersive AR, VR, and XR experiences, 3D projection mapping, AI-powered systems, software solutions, and the hardware infrastructure that powers it all. From holographic installations and large-scale visual experiences to industry-focused mobile applications, KIVAS designs, develops, and deploys everything under one roof — delivering seamless, end-to-end innovation.",
  "With a deep understanding of the next-generation technology ecosystem, KIVAS creates powerful 3D visualisations and intelligent software solutions tailored not just to meet expectations, but to redefine reality. We partner with brands, enterprises, and organisations from global corporations to event agencies who demand high-performance technology, reject the ordinary, and seek human experiences that feel truly extraordinary.",
];

export default function AboutPage() {
  return (
    <main className="relative min-h-screen bg-transparent overflow-x-hidden pt-28 pb-24 z-10">
      {/* Decorative ambient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 relative z-10">
        {/* Eyebrow label */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
        >
          <div className="w-4 h-px bg-indigo-500/50" />
          About Us
        </motion.div>

        {/* Hero heading */}
        <div className="mb-20 md:mb-28">
          <motion.h1
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="font-Montserrat text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white"
          >
            Redefining
            <br />
            <motion.span
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 1.0,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.25,
              }}
              className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif"
            >
              Digital Boundaries
            </motion.span>
          </motion.h1>
        </div>

        {/* Single glassmorphism card */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{ y: -6, transition: { duration: 0.3 } }}
          className="relative group w-full"
        >
          <div className="relative h-full p-8 md:p-10 rounded-2xl border border-white/10 dark:border-white/[0.08] bg-white/5 dark:bg-white/[0.03] backdrop-blur-xl overflow-hidden transition-colors duration-500">
            {/* Noise texture overlay */}
            <div
              className="absolute inset-0 opacity-[0.025] pointer-events-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E")`,
                backgroundSize: "200px 200px",
              }}
            />

            {/* Hover-reveal orb */}
            <div
              className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-700 pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)",
                filter: "blur(50px)",
              }}
            />

            {/* Accent line at top */}
            <div className="relative z-10 flex items-center gap-3 mb-8">
              <div
                className="w-1 h-8 rounded-full"
                style={{
                  background: "linear-gradient(to bottom, #6366f1, transparent)",
                }}
              />
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/30 to-transparent" />
            </div>

            {/* Paragraphs */}
            <div className="relative z-10 flex flex-col gap-6">
              {aboutParagraphs.map((paragraph, pIdx) => (
                <p
                  key={pIdx}
                  className="font-Montserrat text-base lg:text-lg text-black/80 dark:text-white/80 leading-relaxed text-justify"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}